import { useState } from 'react';
import {
  MdCalendarMonth,
  MdChevronRight,
  MdClose,
  MdCreditCard,
  MdDescription,
  MdDownload,
  MdLogout,
  MdMenu,
  MdMenuBook,
  MdMessage,
  MdOpenInNew,
  MdPhone
} from 'react-icons/md';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import LogoutModal from '../components/LogoutModal';
import { PageHeaderProvider, usePageHeader } from '../contexts/PageHeaderContext';
import { clearAuth } from '../utils/auth.util';
import { getActiveUserInfo } from '../utils/localStore.util';

const navItems = [
  { path: '/dashboard', label: 'Dashboard', icon: MdCalendarMonth },
  // { path: '/dashboard', label: 'Dashboard', icon: MdDashboard },
  { path: '/documents', label: 'Documents', icon: MdDescription },
  { path: '/billing', label: 'Billing', icon: MdCreditCard },
  { path: '/submit-request', label: 'Submit Request', icon: MdMessage },
  { path: '/downloads', label: 'Downloads', icon: MdDownload },
  { path: '/contact', label: 'Contact Info', icon: MdPhone },
  { path: '/client-handbook', label: 'Client Handbook', icon: MdMenuBook },
];

function LayoutInner() {
  const navigate = useNavigate();
  const userInfo = getActiveUserInfo();
  const [isLogoutOpen, setIsLogoutOpen] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { header } = usePageHeader();

  const handleLogout = () => {
    clearAuth();
    setIsLogoutOpen(false);
    navigate('/login');
  };

  return (
    <div className="flex h-screen bg-slate-50 overflow-hidden">
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      <aside
        className={`
          fixed lg:static inset-y-0 left-0 z-50
          w-64 bg-white border-r border-slate-100 shadow-sm
          flex flex-col transition-transform duration-300 ease-in-out
          ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
        `}
      >
        <div className="flex items-center justify-between px-4 sm:px-4 py-3.5 border-b border-slate-100 flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 flex items-center justify-center">
              <img
                src="/logo192.png"
                alt="Logo"
                className="w-full h-full object-contain"
              />
            </div>
            <div>
              <h1 className="text-sm font-bold text-slate-800 leading-none">
                Home Care Admin Experts
              </h1>
            </div>
          </div>
          <button
            onClick={() => setSidebarOpen(false)}
            className="lg:hidden text-slate-400 hover:text-slate-600 p-1 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-200"
            aria-label="Close navigation"
          >
            <MdClose size={18} />
          </button>
        </div>

        <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
          <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider px-3 mb-3">
            Navigation
          </p>
          {navItems.map(({ path, label, icon: Icon }) => (
            <NavLink
              key={path}
              to={path}
              onClick={() => setSidebarOpen(false)}
              className={({ isActive }) =>
                `group flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all duration-150 ${isActive
                  ? 'bg-brand-700 text-white shadow-brand-200'
                  : 'text-slate-600 hover:bg-brand-50 hover:text-brand-700'
                }`
              }
            >
              {({ isActive }) => (
                <>
                  <Icon
                    size={18}
                    className={
                      isActive ? 'text-white' : 'text-slate-400 group-hover:text-brand-600'
                    }
                  />
                  <span className="flex-1">{label}</span>
                  {isActive && <MdChevronRight size={16} className="text-brand-300" />}
                </>
              )}
            </NavLink>
          ))}

          <button
            onClick={() => window.open('https://www.iheart-hc.com/', '_blank')}
            className="w-full group flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium text-slate-600 hover:bg-brand-50 hover:text-brand-700 transition-all duration-150 focus:outline-none focus:ring-2 focus:ring-brand-200"
          >
            <MdOpenInNew size={18} className="text-slate-400 group-hover:text-brand-600" />
            <span className="flex-1 text-left">Website</span>
          </button>
        </nav>

        <div className="flex-shrink-0 px-3 py-4 border-t border-slate-100">
          <button
            onClick={() => { setSidebarOpen(false); setIsLogoutOpen(true); }}
            className="w-full group flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium text-red-500 hover:bg-red-50 hover:text-red-600 transition-all duration-150 focus:outline-none focus:ring-2 focus:ring-red-200"
          >
            <MdLogout size={18} className="text-red-400 group-hover:text-red-500" />
            <span className="flex-1 text-left">Log Out</span>
          </button>
        </div>
      </aside>

      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        <header className="bg-white/90 backdrop-blur border-b border-slate-100 px-4 sm:px-6 py-3 flex items-center justify-between shadow-sm flex-shrink-0 sticky top-0 z-30">
          <div className="flex items-center gap-3 min-w-0">
            <button
              type="button"
              onClick={() => setSidebarOpen(true)}
              className="lg:hidden w-8 h-8 rounded-xl bg-white border border-slate-200 shadow-sm flex items-center justify-center text-slate-600 hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-brand-200"
              aria-label="Open navigation"
            >
              <MdMenu size={18} />
            </button>

            <div className="min-w-0">
              {header.title && (
                <p className="text-sm sm:text-base font-bold text-slate-800 leading-tight truncate">
                  {header.title}
                </p>
              )}
              {header.description && (
                <p className="text-xs text-slate-400 leading-tight truncate hidden sm:block">
                  {header.description}
                </p>
              )}
            </div>
          </div>

          <div className="flex items-center gap-3 sm:gap-4 min-w-0 flex-shrink-0">
            <div className="min-w-0 text-right">
              <p className="text-sm font-semibold text-slate-800 truncate">
                {(userInfo?.name as string) || 'User'}
              </p>
              <p className="text-xs text-slate-400 truncate">
                {(userInfo?.company as string) || 'HCAE Client Portal'}
              </p>
            </div>

            {userInfo?.avatar_url ? (
              <img
                src={userInfo.avatar_url as string}
                alt="avatar"
                className="sm:w-9 sm:h-9 w-7 h-7 rounded-full object-cover ring-2 ring-brand-200 flex-shrink-0"
              />
            ) : (
              <div className="sm:w-9 sm:h-9 w-7 h-7 rounded-full bg-gradient-to-br from-brand-400 to-brand-600 flex items-center justify-center text-white font-semibold text-sm flex-shrink-0">
                {(userInfo?.name as string)?.[0]?.toUpperCase() || 'U'}
              </div>
            )}
          </div>
        </header>

        <main className="flex-1 overflow-y-auto">
          <div className="p-2 sm:p-3 h-full">
            <Outlet />
          </div>
        </main>
      </div>

      <LogoutModal
        isOpen={isLogoutOpen}
        onClose={() => setIsLogoutOpen(false)}
        onConfirm={handleLogout}
      />
    </div>
  );
}

export default function WebLayout() {
  return (
    <PageHeaderProvider>
      <LayoutInner />
    </PageHeaderProvider>
  );
}
